/**
 * RecentAdditionsPanel - Ghost Transaction Detection
 * Extracted from DevPage.tsx for modularity
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, Button } from '@/components/shared';
import { db, Transaction } from '@services/db';

type TimeFilter = 'today' | '24h' | '7days' | 'all';

interface RecentTx {
  id: string;
  date: Date;
  createdAt: Date;
  amount: number;
  type: 'income' | 'expense' | 'transfer';
  categoryName: string | null;
  categoryId: string | null;
  description: string;
  deletedAt: Date | null;
  ghostFlags: string[];
  isAdjustment: boolean;  // Balance adjustment (not a ghost)
}

interface RecentAdditionsPanelProps {
  onDataChange: () => void;
}

export function RecentAdditionsPanel({ onDataChange }: RecentAdditionsPanelProps) {
  const [filter, setFilter] = useState<TimeFilter>('today');
  const [transactions, setTransactions] = useState<RecentTx[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionResult, setActionResult] = useState<string | null>(null);
  const [hideAdjustments, setHideAdjustments] = useState(true);  // Hide by default

  // Load recent transactions with ghost detection
  const loadRecentTransactions = useCallback(async () => {
    setLoading(true);
    try {
      // Determine time cutoff based on filter
      const now = new Date();
      let cutoffMs: number;
      switch (filter) {
        case 'today':
          cutoffMs = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
          break;
        case '24h':
          cutoffMs = now.getTime() - 24 * 60 * 60 * 1000;
          break;
        case '7days':
          cutoffMs = now.getTime() - 7 * 24 * 60 * 60 * 1000;
          break;
        default:
          cutoffMs = 0; // All time
      }

      // Get all transactions and categories
      const allTxns = await db.transactions.toArray();
      const categories = await db.categories.toArray();
      const categoryMap = new Map(categories.map(c => [c.id, c.name]));

      // Filter by createdAt
      const recentRaw = allTxns.filter(tx => {
        const createdTime = tx.createdAt instanceof Date
          ? tx.createdAt.getTime()
          : new Date(tx.createdAt).getTime();
        return createdTime >= cutoffMs;
      });

      // Sort by createdAt descending
      recentRaw.sort((a, b) => {
        const aTime = a.createdAt instanceof Date ? a.createdAt.getTime() : new Date(a.createdAt).getTime();
        const bTime = b.createdAt instanceof Date ? b.createdAt.getTime() : new Date(b.createdAt).getTime();
        return bTime - aTime;
      });

      // Check if transaction is a balance adjustment
      const isBalanceAdjustment = (tx: Transaction): boolean => {
        return tx.description?.toLowerCase().includes('[adjustment]') ||
               tx.description?.toLowerCase().startsWith('adjustment');
      };

      // Ghost detection logic - SKIP adjustments (they're intentional, not ghosts)
      const detectGhostFlags = (tx: Transaction, allRecent: Transaction[]): string[] => {
        // Balance adjustments are NOT ghosts - they're intentional
        if (isBalanceAdjustment(tx)) {
          return [];  // No flags for adjustments
        }

        const flags: string[] = [];

        // 1. No category (except transfers and adjustments)
        if (!tx.categoryId && tx.type !== 'transfer') {
          flags.push('no-category');
        }

        // 2. Duplicate detection: same amount + category + type within 10 minutes
        const txCreated = tx.createdAt instanceof Date ? tx.createdAt.getTime() : new Date(tx.createdAt).getTime();
        const duplicates = allRecent.filter(other => {
          if (other.id === tx.id) return false;
          if (isBalanceAdjustment(other)) return false;  // Don't compare with adjustments
          const otherCreated = other.createdAt instanceof Date ? other.createdAt.getTime() : new Date(other.createdAt).getTime();
          const timeDiff = Math.abs(txCreated - otherCreated);
          return (
            other.amount === tx.amount &&
            other.categoryId === tx.categoryId &&
            other.type === tx.type &&
            timeDiff < 10 * 60 * 1000 // 10 minutes
          );
        });
        if (duplicates.length > 0) {
          flags.push('duplicate');
        }

        return flags;
      };

      // Map to RecentTx with ghost flags and adjustment marker
      const recent: RecentTx[] = recentRaw.map(tx => ({
        id: tx.id!,
        date: tx.date instanceof Date ? tx.date : new Date(tx.date),
        createdAt: tx.createdAt instanceof Date ? tx.createdAt : new Date(tx.createdAt),
        amount: tx.amount,
        type: tx.type as 'income' | 'expense' | 'transfer',
        categoryName: tx.categoryId ? (categoryMap.get(tx.categoryId) || null) : null,
        categoryId: tx.categoryId || null,
        description: tx.description || '',
        deletedAt: tx.deletedAt ? (tx.deletedAt instanceof Date ? tx.deletedAt : new Date(tx.deletedAt)) : null,
        ghostFlags: detectGhostFlags(tx, recentRaw),
        isAdjustment: isBalanceAdjustment(tx),
      }));

      setTransactions(recent);
    } catch (err) {
      console.error('[RecentAdditions] Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadRecentTransactions();
  }, [loadRecentTransactions]);

  // Soft-delete a transaction (marks for sync)
  const handleSoftDelete = async (id: string) => {
    try {
      const tx = await db.transactions.get(id);
      if (tx) {
        tx.deletedAt = new Date();
        tx.updatedAt = new Date();
        tx.syncStatus = 'pending';  // CRITICAL: Mark for sync so delete propagates
        await db.transactions.put(tx);
        setActionResult(`[OK] Soft-deleted transaction (will sync)`);
        loadRecentTransactions();
        onDataChange();
      }
    } catch (err) {
      setActionResult(`[ERROR] ${String(err)}`);
    }
  };

  // Hard-delete a transaction (permanent)
  const handleHardDelete = async (id: string) => {
    try {
      await db.transactions.delete(id);
      setActionResult(`[OK] Permanently deleted transaction`);
      loadRecentTransactions();
      onDataChange();
    } catch (err) {
      setActionResult(`[ERROR] ${String(err)}`);
    }
  };

  // Restore a soft-deleted transaction (marks for sync)
  const handleRestore = async (id: string) => {
    try {
      const tx = await db.transactions.get(id);
      if (tx) {
        tx.deletedAt = undefined;
        tx.updatedAt = new Date();
        tx.syncStatus = 'pending';  // Mark for sync so restore propagates
        await db.transactions.put(tx);
        setActionResult(`[OK] Restored transaction (will sync)`);
        loadRecentTransactions();
        onDataChange();
      }
    } catch (err) {
      setActionResult(`[ERROR] ${String(err)}`);
    }
  };

  // Filter adjustments if hideAdjustments is true
  const visibleTransactions = hideAdjustments
    ? transactions.filter(tx => !tx.isAdjustment)
    : transactions;

  // Count stats
  const totalCount = visibleTransactions.length;
  const adjustmentCount = transactions.filter(tx => tx.isAdjustment).length;
  const ghostCount = visibleTransactions.filter(tx => tx.ghostFlags.length > 0 && !tx.deletedAt).length;
  const deletedCount = visibleTransactions.filter(tx => tx.deletedAt).length;

  // Format helpers
  const formatTime = (d: Date) => d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  const formatDate = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <Card>
      <CardHeader
        title="Recent Additions"
        subtitle="Transactions created recently - detect and remove ghost entries"
      />

      {/* Header with filter, toggles, and stats */}
      <div className="recent-additions-header">
        <div className="recent-controls">
          <div className="recent-filter">
            <span className="recent-filter-label">Show:</span>
            <select
              className="recent-filter-select"
              value={filter}
              onChange={(e) => setFilter(e.target.value as TimeFilter)}
            >
              <option value="today">Today</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7days">Last 7 Days</option>
              <option value="all">All Time</option>
            </select>
          </div>
          {/* Hide adjustments toggle */}
          <label className="recent-toggle">
            <input
              type="checkbox"
              checked={hideAdjustments}
              onChange={(e) => setHideAdjustments(e.target.checked)}
            />
            <span>Hide adjustments{adjustmentCount > 0 ? ` (${adjustmentCount})` : ''}</span>
          </label>
        </div>
        <div className="recent-stats">
          <div className="recent-stat">
            <span className="recent-stat-count">{totalCount}</span>
            <span className="recent-stat-label">shown</span>
          </div>
          {ghostCount > 0 && (
            <div className="recent-stat ghosts">
              <span className="recent-stat-count">{ghostCount}</span>
              <span className="recent-stat-label">flagged</span>
            </div>
          )}
          {deletedCount > 0 && (
            <div className="recent-stat">
              <span className="recent-stat-count">{deletedCount}</span>
              <span className="recent-stat-label">deleted</span>
            </div>
          )}
        </div>
      </div>

      {/* Action result */}
      {actionResult && (
        <p className={`balance-result ${actionResult.includes('[OK]') ? 'success' : 'error'}`}>
          {actionResult}
        </p>
      )}

      {/* Transaction list */}
      {loading ? (
        <div className="loading">Loading transactions...</div>
      ) : visibleTransactions.length === 0 ? (
        <div className="recent-empty">
          <div className="recent-empty-icon">[=]</div>
          <p>No transactions created in this time period{hideAdjustments && adjustmentCount > 0 ? ` (${adjustmentCount} adjustments hidden)` : ''}</p>
        </div>
      ) : (
        <div className="recent-additions-list">
          {visibleTransactions.map((tx) => {
            const isGhost = tx.ghostFlags.length > 0;
            const isDeleted = !!tx.deletedAt;
            const rowClass = `recent-tx-row ${isGhost && !isDeleted ? 'ghost' : ''} ${isDeleted ? 'deleted' : ''} ${tx.isAdjustment ? 'adjustment' : ''}`;

            return (
              <div key={tx.id} className={rowClass}>
                <div className="recent-tx-info">
                  <div className="recent-tx-main">
                    <span className={`recent-tx-amount ${tx.type}`}>
                      {tx.type === 'income' ? '+' : tx.type === 'expense' ? '-' : ''}
                      ${tx.amount.toFixed(2)}
                    </span>
                    <span className={`recent-tx-category ${!tx.categoryName ? 'none' : ''}`}>
                      {tx.categoryName || (tx.type === 'transfer' ? 'Transfer' : 'No category')}
                    </span>
                    {tx.description && (
                      <span className="recent-tx-desc">{tx.description}</span>
                    )}
                    {/* Adjustment badge (info, not warning) */}
                    {tx.isAdjustment && !isDeleted && (
                      <span className="adjustment-badge">BAL ADJ</span>
                    )}
                    {/* Ghost flags (only DUP and NO-CAT now, not adjustments) */}
                    {tx.ghostFlags.length > 0 && !isDeleted && (
                      <div className="ghost-flags">
                        {tx.ghostFlags.map((flag) => (
                          <span key={flag} className={`ghost-flag ${flag}`}>
                            {flag === 'duplicate' ? 'DUP' : 'NO-CAT'}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Deleted flag */}
                    {isDeleted && <span className="deleted-flag">DELETED</span>}
                  </div>
                  <div className="recent-tx-meta">
                    <span className="recent-tx-created">
                      Created: {formatDate(tx.createdAt)} {formatTime(tx.createdAt)}
                    </span>
                    <span className="recent-tx-date">
                      Date: {formatDate(tx.date)}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="recent-tx-actions">
                  {isDeleted ? (
                    <>
                      <Button variant="secondary" onClick={() => handleRestore(tx.id)}>
                        Restore
                      </Button>
                      <Button variant="danger" onClick={() => handleHardDelete(tx.id)}>
                        Purge
                      </Button>
                    </>
                  ) : (
                    <Button variant="danger" onClick={() => handleSoftDelete(tx.id)}>
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
